import { IMAGES, VALUES } from "../data/site";
import { SectionEyebrow } from "../components/SectionEyebrow";
import { Compass, Eye, Target, Award } from "lucide-react";
import { Link } from "react-router-dom";

export default function QuienesSomos() {
    return (
        <>
            {/* Hero */}
            <section className="topo-bg py-20 lg:py-28" data-testid="about-hero">
                <div className="container-fe">
                    <div className="max-w-3xl animate-fade-up">
                        <SectionEyebrow>Quiénes somos</SectionEyebrow>
                        <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-navy leading-[1.05]">
                            Una historia de <span className="text-brand-green">15 años</span> tejida en el territorio.
                        </h1>
                        <p className="mt-7 text-lg text-muted-foreground leading-relaxed">
                            Fundada en el año 2014 en la ciudad de Ibagué y con campo de acción
                            primario en la Vereda La Coqueta desde el año 2012. Desde entonces,
                            hemos trabajado con niños, jóvenes, familias y adultos mayores del
                            Tolima, abriendo caminos y promoviendo su bienestar y desarrollo
                            integral.
                        </p>
                    </div>
                </div>
            </section>

            {/* Story split */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-fe grid lg:grid-cols-2 gap-14 items-center">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-black/5">
                            <img src={IMAGES.volunteers_christmas} alt="Equipo voluntario" className="w-full h-full object-cover" />
                        </div>
                        <div className="aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-black/5 mt-10">
                            <img src={IMAGES.children_community} alt="Comunidad" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div>
                        <SectionEyebrow>Nuestra historia</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-4xl text-brand-navy leading-tight">
                            Del barrio al mundo, de la mano de la comunidad.
                        </h2>
                        <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
                            <p>
                                Creemos en el poder de las comunidades para transformar realidades.
                                Por eso, cada proyecto, actividad y alianza nace escuchando a las
                                personas, caminando junto a ellas y construyendo oportunidades que
                                impacten generaciones.
                            </p>
                            <p>
                                Hoy somos un equipo multidisciplinario respaldado por más de 30 voluntarios
                                activos, alianzas estratégicas como el programa <strong className="text-brand-navy">Aflora</strong> de
                                la Fundación Bolívar Davivienda, y el cariño de cientos de familias que nos han
                                permitido caminar a su lado.
                            </p>
                            <p>
                                Lo que no ha cambiado en 15 años: nuestra convicción de que cada persona es un
                                proyecto digno y posible.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission, Vision */}
            <section className="py-20 lg:py-28 bg-brand-cream">
                <div className="container-fe grid lg:grid-cols-3 gap-6">
                    {[
                        {
                            icon: <Target size={24} />,
                            title: "Misión",
                            text: "Somos una fundación que trabaja a favor de la infancia y las comunidades en condición de vulnerabilidad promoviendo el bienestar y el desarrollo integral de nuestros niños, niñas y adolescentes en su entorno.",
                        },
                        {
                            icon: <Eye size={24} />,
                            title: "Visión",
                            text: "Al 2030 la Fundación Formar Estructura será reconocida por ser gestora de la formación de seres integrales que puedan desempeñarse como líderes capaces de transformar realidades, además de fomentar una conciencia ambiental, humana y social. Así mismo llegará a ser una fundación autosostenible con impacto en comunidades en condiciones de riesgo biosocial.",
                        },
                        {
                            icon: <Compass size={24} />,
                            title: "Enfoque",
                            text: "Desarrollo humano, educación, cultura y comunidad. No respondemos con asistencialismo: co-creamos con las personas las condiciones para que sus capacidades se desplieguen.",
                        },
                    ].map((it) => (
                        <div key={it.title} className="card-flat" data-testid={`about-pillar-${it.title.toLowerCase()}`}>
                            <div className="h-12 w-12 rounded-xl bg-brand-green text-white flex items-center justify-center">
                                {it.icon}
                            </div>
                            <h3 className="mt-5 font-display font-bold text-2xl text-brand-navy">{it.title}</h3>
                            <p className="mt-3 text-muted-foreground leading-relaxed">{it.text}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Values */}
            <section className="py-20 lg:py-28 bg-white">
                <div className="container-fe">
                    <div className="max-w-2xl">
                        <SectionEyebrow>Nuestros valores</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl text-brand-navy leading-tight">
                            Seis principios que guían cada decisión.
                        </h2>
                    </div>
                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {VALUES.map((v, i) => (
                            <div key={v.title} className="card-flat">
                                <div className="font-display text-2xl font-bold text-brand-green">{String(i + 1).padStart(2, "0")}</div>
                                <h3 className="mt-3 font-display text-xl font-semibold text-brand-navy">{v.title}</h3>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Leadership */}
            <section className="py-20 lg:py-28 bg-brand-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 grain-bg opacity-30 pointer-events-none" />
                <div className="container-fe relative grid lg:grid-cols-5 gap-10 items-center">
                    <div className="lg:col-span-2">
                        <div className="aspect-square rounded-3xl overflow-hidden ring-4 ring-white/10">
                            <img src={IMAGES.volunteer_painting} alt="Liderazgo en acción" className="w-full h-full object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-3">
                        <SectionEyebrow className="[&>span:last-child]:text-brand-green">Liderazgo</SectionEyebrow>
                        <h2 className="mt-4 font-display font-bold text-3xl lg:text-5xl leading-tight">
                            Un liderazgo cercano, comunitario, profundamente humano.
                        </h2>
                        <p className="mt-6 text-white/80 leading-relaxed text-lg">
                            La fundación es liderada por un equipo con vocación social, formación
                            profesional diversa y un compromiso de largo plazo con el territorio. La
                            dirección general impulsa una visión transformadora: convencida de que el
                            cambio se construye desde abajo, con paciencia y consistencia.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm">
                                <Award size={16} className="text-brand-green" />
                                Reconocidos por programa Aflora
                            </div>
                            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm">
                                <Award size={16} className="text-brand-green" />
                                Aliados internacionales
                            </div>
                        </div>
                        <Link to="/transparencia" className="btn-primary-pill mt-8" data-testid="about-transparency-cta">
                            Ver transparencia institucional
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
